<template>
  <background>
    <div class="page">
      <logo v-show="showLogo && !redirectPage" />
      <transition name="fade">
        <div class="login" v-show="show" style="animation-duration: 0.8s">
          <div class="login__title">{{ loginTitles.welcome }}</div>
          <ValidationObserver v-slot="{ passes, valid }">
            <div class="login__form">
              <div class="login__input-group">
                <div>
                  <ValidationProvider
                    name="email"
                    rules="required|email"
                    v-slot="{ errors }"
                    class="login__input-container"
                  >
                    <label class="login__input-label">{{
                      loginTitles.email
                    }}</label>
                    <div class="login__input-box">
                      <email class="login__icon" />
                      <input
                        v-model="login.email"
                        type="email"
                        class="login__input"
                        :class="errors[0] && 'error'"
                        placeholder="example@sellia.com"
                        @keyup.enter="$refs.password.focus()"
                      />
                    </div>
                    <transition name="fade">
                      <span v-if="errors[0]" class="login__input-error">{{
                        errors[0]
                      }}</span>
                    </transition>
                  </ValidationProvider>
                </div>
                <div>
                  <ValidationProvider
                    name="password"
                    rules="required|min:2"
                    v-slot="{ errors }"
                    class="login__input-container"
                  >
                    <label class="login__input-label">{{
                      loginTitles.password
                    }}</label>
                    <div class="login__input-password">
                      <button
                        class="login__input-password-icon"
                        @click="toggleShow"
                      >
                        <eye v-if="showPassword" />
                        <eye-off v-else />
                      </button>
                      <div class="login__input-box">
                        <lock class="login__icon" />
                        <input
                          ref="password"
                          v-model="login.password"
                          :type="passwordInput"
                          class="login__input"
                          :class="errors[0] && 'error'"
                          placeholder="********"
                          @keyup.enter="passes(loginSubmit)"
                        />
                      </div>
                    </div>
                    <transition name="fade">
                      <span v-if="errors[0]" class="login__input-error">{{
                        errors[0]
                      }}</span>
                    </transition>

                    <p v-show="errorMsg" class="login__forgot-alert">
                      {{ errorMsg }}
                    </p>
                  </ValidationProvider>
                  <div class="login__forgot" @click="toggleRedirect">
                    <router-link
                      to="/forgot-password"
                      class="login__forgot-link"
                    >
                      {{ loginTitles.forgot }}
                    </router-link>
                  </div>
                </div>
              </div>
              <button
                ref="clickBtn"
                :disabled="!valid || (isLoading && valid)"
                @click="
                  passes(loginSubmit);
                  animateRipple($event);
                "
                class="login__button"
                :class="
                  (isLoading && valid && 'disabled') || (!valid && 'disabled')
                "
              >
                {{ loginTitles["log-in"] }}

                <transition-group>
                  <span
                    class="button-effect"
                    :ref="'button-effect' + i"
                    :key="'button-effect' + i"
                    v-for="(val, i) in activeRipples"
                    :style="{ top: val.y + 'px', left: val.x + 'px' }"
                    v-on:animationend="rippleEnd(i)"
                  >
                  </span>
                </transition-group>
              </button>
            </div>
          </ValidationObserver>
        </div>
      </transition>
    </div>
  </background>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Eye from '@/assets/images/eye.svg';
import EyeOff from '@/assets/images/eye-off.svg';
import Email from '@/assets/images/email.svg';
import Lock from '@/assets/images/lock.svg';
import Logo from './Logo.vue';
import api from '@/api';
import jwt from 'jsonwebtoken';
import moment from 'moment';
import errorHandler from '../error.handler';
import Background from './Background.vue';

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    Eye,
    EyeOff,
    Email,
    Lock,
    Logo,
    Background,
  },
  mounted() {
    if (this.version) {
      this.version = Number(this.version);
    }
    this.validateSession();

    setTimeout(() => {
      this.show = true;
    }, 300);
  },
  created(){
    setTimeout(() => {
      this.showLogo = true;
    }, 300);
  },
  data: () => ({
    login: {
      email: "",
      password: "",
    },
    errorMsg: null,
    token: localStorage.getItem('AUTH_TOKEN'),
    version: localStorage.getItem('AUTH_VERSION'),
    passwordInput: "password",
    showPassword: true,
    show: false,
    showLogo: false,
    redirectPage: false,
    ripples: [],
    isLoading: false,
  }),
  computed: {
    loginTitles(){
      return this.$t("login");
    },
    activeRipples() {
      return this.ripples.filter(r => r.show);
    },
  },
  methods: {
    loginSubmit() {
      this.isLoading = true;
      this.errorMsg = null
      api.login(this.login)
        .then((res) => {
          if (res.data && res.data.token) {
            this.version = res.data.version ?? 1;
            this.token = res.data.token;
            localStorage.setItem('AUTH_TOKEN', this.token);
            localStorage.setItem('AUTH_VERSION', this.version);
            this.redirect();
            return
          }
          throw new Error('Cannot set token');
        })
        .catch((e) => {
          errorHandler.logErrors(e);
          if (e.response.status === 401) {
            this.errorMsg = e.response.data.messages
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    redirect() {
      if (this.version !== 2) {
          window.location.href = `${process.env.VUE_APP_V1_BASE_URL}/autologin/${this.token}`;
      } else {
          const user = jwt.decode(this.token);

          if (this.isValidUser(user) && this.isValidToken(user.exp)) {
              const baseUrl = user.role === 'agent' ? process.env.VUE_APP_CHAT_BASE_URL : process.env.VUE_APP_ADMIN_BASE_URL;
              window.location.href = `${baseUrl}?data=${this.token}`;
          }
      }
    },
    validateSession() {
      if (this.token && this.version) {
        this.redirect();
      }
    },
    isValidUser(user) {
        return user && user.id && user.exp && user.role;
    },
    isValidToken(exp) {
      const expires = moment.unix(exp);
      const today = moment(new Date()).format();
      const expired = expires.format() <= today;

      if (expired) {
        this.removeToken();
      }

      return !expired;
    },
    removeToken() {
      localStorage.removeItem('AUTH_TOKEN');
      localStorage.removeItem('AUTH_VERSION');
    },
    toggleShow(){
      this.showPassword = !this.showPassword;

      this.passwordInput = this.showPassword ? 'password' : 'text';
    },
    toggleRedirect(){
      this.redirectPage = !this.redirectPage;
    },
    animateRipple(e) {
      const el = this.$refs.clickBtn;
      const pos = el.getBoundingClientRect();
      this.ripples.push({
        x: e.clientX - pos.left,
        y: e.clientY - pos.top,
        show: true,
      });
    },
    rippleEnd(i) {
      this.ripples[i].show = false;
    },
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/views/_login.scss";
</style>
